import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getFeaturedPromoCode } from '../features/landing/promoBannerApi';

function useCountdown(expiresAt: string | null) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt) return null;

  const remainingMs = new Date(expiresAt).getTime() - now;
  if (remainingMs <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };

  const totalSeconds = Math.floor(remainingMs / 1000);
  return {
    expired: false,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function PromoBanner() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const { data: promo } = useQuery({
    queryKey: ['featured-promo-code'],
    queryFn: getFeaturedPromoCode,
    staleTime: 5 * 60 * 1000,
  });

  const countdown = useCountdown(promo?.expiresAt ?? null);

  // Le code peut avoir expiré depuis le dernier fetch (staleTime 5 min) : on masque
  // dès que le compte à rebours atteint zéro, sans attendre le prochain refetch.
  if (!promo || dismissed || countdown?.expired) return null;

  const discountLabel =
    promo.discountType === 'PERCENTAGE' ? `-${promo.discountValue}%` : `-${promo.discountValue} €`;

  return (
    <div className="relative bg-seal-500 font-manuscript">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-2 gap-y-1 px-10 py-2.5 text-center text-sm text-paper-50">
        <span className="font-semibold">{discountLabel}</span>
        <span>{t('promoBanner.withCode')}</span>
        <span className="rounded-sm bg-paper-50/15 px-2 py-0.5 font-mono text-xs font-semibold tracking-wide">
          {promo.code}
        </span>
        {countdown && (
          <span className="text-xs text-paper-50/90">
            {countdown.days > 0
              ? t('promoBanner.expiresInDays', { count: countdown.days })
              : t('promoBanner.expiresInTime', {
                  time: `${pad(countdown.hours)}:${pad(countdown.minutes)}:${pad(countdown.seconds)}`,
                })}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('promoBanner.dismiss')}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1.5 text-paper-50/80 hover:text-paper-50"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}
